import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TrackingScript } from "@/components/TrackingScript";
import { CountryProvider } from "@/contexts/CountryContext";
import { FEATURE_FLAGS } from "@/config/featureFlags";

// Layout components stay static (needed immediately for route matching)
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import AdminDashboardLayout from "./pages/admin/AdminDashboardLayout";

// All page components lazy-loaded
const Index = lazy(() => import("./pages/Index"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ReferralProgram = lazy(() => import("./pages/ReferralProgram"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LanderPage = lazy(() => import("./pages/Lander"));
const TrackLinkRedirect = lazy(() => import("./pages/TrackLinkRedirect"));
const Chatters = lazy(() => import("./pages/Chatters"));
const FanvueCallback = lazy(() => import("./pages/FanvueCallback"));
const Avatar = lazy(() => import("./pages/Avatar"));
const ImageTool = lazy(() => import("./pages/ImageTool"));
const VideoTool = lazy(() => import("./pages/VideoTool"));
const AssetsTool = lazy(() => import("./pages/AssetsTool"));
const BrowserAvatars = lazy(() => import("./pages/BrowserAvatars"));
const PublicInvoices = lazy(() => import("./pages/PublicInvoices"));

// Dashboard pages
const DashboardTasks = lazy(() => import("./pages/dashboard/Tasks"));
const DashboardEducation = lazy(() => import("./pages/dashboard/Education"));
const DashboardCreators = lazy(() => import("./pages/dashboard/Creators"));
const DashboardVideo = lazy(() => import("./pages/dashboard/Video"));
const DashboardVideoHistory = lazy(() => import("./pages/dashboard/VideoHistory"));
const DashboardDuplicating = lazy(() => import("./pages/dashboard/Duplicating"));
const DashboardDuplicatingHistory = lazy(() => import("./pages/dashboard/DuplicatingHistory"));
const DashboardTracking = lazy(() => import("./pages/dashboard/Tracking"));
const DashboardTrackingDfans = lazy(() => import("./pages/dashboard/TrackingDfans"));
const DashboardLander = lazy(() => import("./pages/dashboard/Lander"));
const DashboardCalendar = lazy(() => import("./pages/dashboard/Calendar"));
const DashboardReferrals = lazy(() => import("./pages/dashboard/Referrals"));
const DashboardPayouts = lazy(() => import("./pages/dashboard/Payouts"));
const DashboardProfile = lazy(() => import("./pages/dashboard/Profile"));
const DashboardInvoices = lazy(() => import("./pages/dashboard/Invoices"));
const DashboardNotifications = lazy(() => import("./pages/dashboard/Notifications"));
const DashboardGoals = lazy(() => import("./pages/dashboard/Goals"));
const DashboardMyTasks = lazy(() => import("./pages/dashboard/MyTasks"));

// Education sub-pages
const EducationCourse = lazy(() => import("./pages/dashboard/education/Course"));
const KnowledgeBotPage = lazy(() => import("./pages/dashboard/education/KnowledgeBotPage"));

// Admin pages
const AdminCourses = lazy(() => import("./pages/admin/Courses"));
const AdminCourseForm = lazy(() => import("./pages/admin/CourseForm"));
const AdminCreators = lazy(() => import("./pages/admin/AdminCreators"));
const AdminEducation = lazy(() => import("./pages/admin/AdminEducation"));
const AdminViralContent = lazy(() => import("./pages/admin/AdminViralContent"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminVideo = lazy(() => import("./pages/admin/AdminVideo"));
const AdminDuplicating = lazy(() => import("./pages/admin/AdminDuplicating"));
const AdminTracking = lazy(() => import("./pages/admin/AdminTracking"));
const AdminLander = lazy(() => import("./pages/admin/AdminLander"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));

const queryClient = new QueryClient();

// Wrapper component for routes
function AppContent() {
  return (
    <>
      <TrackingScript />
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      }>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/referral-program" element={<ReferralProgram />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/fanvue/callback" element={<FanvueCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/chatters" element={<Chatters />} />
          <Route path="/invoices" element={<PublicInvoices />} />
          {/* Admin layout routes */}
          <Route path="/admin" element={<AdminDashboardLayout />}>
            <Route index element={<Navigate to="creators" replace />} />
            <Route path="dashboard" element={<Navigate to="/admin/creators" replace />} />
            <Route path="dashboard/creators" element={<Navigate to="/admin/creators" replace />} />

            <Route path="home" element={<Navigate to="/admin/creators" replace />} />
            <Route path="home/my-tasks" element={<DashboardMyTasks />} />

            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="education" element={<AdminEducation />} />
            <Route path="education/templates/:templateId" element={<Navigate to="/admin/education" replace />} />
            <Route path="creators" element={<AdminCreators />} />
            <Route path="video" element={<AdminVideo />} />
            <Route path="duplicating" element={<AdminDuplicating />} />
            <Route path="tracking" element={<AdminTracking />} />
            <Route path="compass" element={<AdminLander />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="viral-content" element={<AdminViralContent />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="referrals/content" element={<AdminReferrals />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="invoices/payments" element={<AdminInvoices />} />
            <Route path="invoices/analytics" element={<AdminInvoices />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="settings/:tab" element={<AdminSettings />} />
            <Route path="chatters" element={<Navigate to="/admin/settings" replace />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="analytics/applications" element={<AdminAnalytics />} />
            <Route path="jobs" element={<Navigate to="/admin/settings" replace />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="courses/new" element={<AdminCourseForm />} />
            <Route path="courses/:courseId/edit" element={<AdminCourseForm />} />
          </Route>
          
          {/* (Admin routes moved into the nested /admin layout route above) */}
          
          {/* App Routes (with sidebar layout) */}
          <Route element={<DashboardLayout />}>
            <Route path="/home" element={<DashboardTasks />} />
            <Route path="/home/daily" element={<DashboardTasks />} />
            <Route path="/home/weekly" element={<DashboardTasks />} />
            <Route path="/home/monthly" element={<DashboardTasks />} />
            <Route path="/home/my-tasks" element={<DashboardMyTasks />} />
            <Route path="/goals" element={<DashboardGoals />} />
            <Route path="/notifications" element={<DashboardNotifications />} />
            <Route path="/creators" element={<DashboardCreators />} />
            <Route path="/creators/approved" element={<DashboardCreators />} />
            <Route path="/creators/pending" element={<DashboardCreators />} />
            <Route path="/education" element={<DashboardEducation />} />
            <Route path="/education/knowledge-bot" element={<KnowledgeBotPage />} />
            <Route path="/education/templates" element={<Navigate to="/education" replace />} />
            <Route path="/education/templates/:templateId" element={<Navigate to="/education" replace />} />
            <Route path="/education/course/:courseId" element={<EducationCourse />} />
            <Route path="/creating/video" element={<DashboardVideo />} />
            <Route path="/creating/video/history" element={<DashboardVideoHistory />} />
            <Route path="/duplicating" element={<DashboardDuplicating />} />
            <Route path="/duplicating/history" element={<DashboardDuplicatingHistory />} />
            <Route
              path="/tracking"
              element={
                FEATURE_FLAGS.dashboardTracking ? (
                  <DashboardTracking />
                ) : (
                  <Navigate to="/home" replace />
                )
              }
            />
            <Route
              path="/tracking/dfans"
              element={
                FEATURE_FLAGS.dashboardTracking ? (
                  <DashboardTrackingDfans />
                ) : (
                  <Navigate to="/home" replace />
                )
              }
            />
            
            <Route path="/compass" element={<DashboardLander />} />
            <Route path="/compass/:tab" element={<DashboardLander />} />
            <Route path="/compass/:tab/:collectionId" element={<DashboardLander />} />
            <Route path="/compass/:tab/:collectionId/:pageId" element={<DashboardLander />} />
            <Route path="/calendar" element={<DashboardCalendar />} />
            {/* Vault page removed */}
            <Route path="/calendar/:mainViewId" element={<DashboardCalendar />} />
            <Route path="/calendar/:mainViewId/:subViewId" element={<DashboardCalendar />} />
            <Route path="/referrals" element={<DashboardReferrals />} />
            <Route path="/referrals/content" element={<DashboardReferrals />} />
            <Route path="/referrals/creators" element={<DashboardReferrals />} />
            <Route path="/referrals/payouts" element={<DashboardReferrals />} />
            <Route path="/payouts" element={<DashboardPayouts />} />
            <Route path="/invoices" element={<DashboardInvoices />} />
            <Route path="/invoices/all" element={<DashboardInvoices />} />
            <Route path="/invoices/draft" element={<DashboardInvoices />} />
            <Route path="/invoices/open" element={<DashboardInvoices />} />
            <Route path="/invoices/paid" element={<DashboardInvoices />} />
            <Route path="/invoices/voided" element={<DashboardInvoices />} />
            <Route path="/invoices/payments" element={<DashboardInvoices />} />
            <Route path="/invoices/analytics" element={<DashboardInvoices />} />
            {/* <Route path="/invoices/daily-report" element={<DashboardInvoices />} /> */}
            <Route path="/invoices/create-invoice" element={<DashboardInvoices />} />
            <Route path="/invoices/edit/:invoiceId" element={<DashboardInvoices />} />
            <Route path="/profile" element={<DashboardProfile />} />
            <Route path="/profile/:tab" element={<DashboardProfile />} />
            <Route path="/dashboard/profile" element={<DashboardProfile />} />
          </Route>
          
          {/* Public Lander Pages - must be before catch-all */}
          <Route path="/l/:slug" element={<LanderPage />} />

          {/* Tracking Link Redirect */}
          <Route path="/t/:collectionId/:slug" element={<TrackLinkRedirect />} />
          <Route path="/t/:slug" element={<TrackLinkRedirect />} />

          {/* Hidden preview routes (not linked from main site) */}
          <Route path="/avatar" element={<Avatar />} />
          <Route path="/image" element={<ImageTool />} />
          <Route path="/video" element={<VideoTool />} />
          <Route path="/assets/*" element={<AssetsTool />} />
          <Route path="/browser-avatars" element={<BrowserAvatars />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CountryProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </CountryProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
